import { hasListener } from '@client/selectors/helpers/listeners';
import { getSessionUserAdminStatus, getUserId, isOwnerOrCollaboratorOrModerator } from '@client/selectors/helpers/session';
import { addFirestoreListener } from '@client/slices/helpers/listeners.reducer';
import { TicketStatuses } from '@sda/enums';
import { getDateTime } from '@sda/utils/date';
import type { PublicShow } from '@sda/interfaces/show';
import type { RootState, AppDispatch } from '@client/store';
import type { ListenerEffectAPI } from '@reduxjs/toolkit';
import type { FetchDataType } from '@sda/sdk/interfaces';

/**
 * Watches/Fetches all purchased, comped, and returned tickets
 * Only available to Side Door admins and showrunners
 * @param  {string} showId
 */
export const fetchAdminTickets = (listenerApi: ListenerEffectAPI<RootState, AppDispatch>, showId: string): void => {
  const state = listenerApi.getState();
  const { dispatch } = listenerApi;

  const listenerName = `list-ticket-${showId}`;
  const listener = hasListener(state, listenerName);

  if (!listener) {
    const firebase = window.Firebase;

    const watchListener = firebase.SDK.Shows.Private().Tickets(showId).watch(
      {
        queries: (query) => [
          query.isEqualTo('showId', showId),
          query.isNotEqualTo('status', TicketStatuses.AVAILABLE),
        ],
      },
    );

    dispatch(addFirestoreListener({ watchListener, name: listenerName }));
  }
};

export const fetchShowTickets = async (listenerApi: ListenerEffectAPI<RootState, AppDispatch>, show: PublicShow & FetchDataType): Promise<void> => {
  const state = listenerApi.getState();
  const { dispatch } = listenerApi;

  const firebase = window.Firebase;

  const userIsAdmin = getSessionUserAdminStatus(state);
  const userId = getUserId(state);
  const isShowCollaborator = isOwnerOrCollaboratorOrModerator(state, show);

  if (userIsAdmin || (!show.isOnlineOnly && isShowCollaborator)) {
    fetchAdminTickets(listenerApi, show.id);
  }

  const products = await firebase.SDK.Products.listBy((query) => [
    query.isEqualTo('showId', show.id),
  ]);

  const listenerName = `list-public-ticket-${show.id}`;

  if (isShowCollaborator) {
    const compListenerName = `${listenerName}-comped`;
    const compListener = hasListener(state, compListenerName);

    // Check for comped tickets
    if (!compListener) {
      const watchListener = firebase.SDK.Shows.Private().Tickets(show.id).watch(
        {
          queries: (query) => [
            query.isEqualTo('status', TicketStatuses.COMPED),
          ],
        },
      );

      dispatch(addFirestoreListener({ watchListener, name: `${listenerName}-comped` }));
    }
  }

  if (products) {
    Object.values(products).forEach((product) => {
      const productListenerName = `${listenerName}-${product.id}`;
      const productListener = hasListener(state, productListenerName);

      if (!productListener) {
        // Check for available tickets
        const watchListener = firebase.SDK.Tickets.watch(
          {
            queries: (query) => [
              query.isEqualTo('showId', show.id),
              query.isEqualTo('stripeSKU', product.id),
              query.isEqualTo('status', TicketStatuses.AVAILABLE),
              query.isLessThan('reservedUntil', getDateTime().toJSDate()),
            ],
            limit: 10,
          },
        );

        dispatch(addFirestoreListener({ watchListener, name: productListenerName }));

        if (userId) {
          // Check for tickets purchased by the current audience member
          const watchPurchasedTicketsListener = firebase.SDK.Users.Private().PurchasedTickets(userId).watch(
            {
              queries: (query) => [
                query.isEqualTo('showId', show.id),
                query.isEqualTo('stripeSKU', product.id),
                query.isIn('status', [
                  TicketStatuses.PURCHASED,
                  TicketStatuses.COMPED,
                  TicketStatuses.PASS,
                ]),
              ],
            },
          );

          dispatch(addFirestoreListener({ watchListener: watchPurchasedTicketsListener, name: `${productListenerName}-purchased` }));
        }
      }
    });
  }
};
